/**
 * Formatting helpers. Compact by default (for cells and axes), exact variants
 * for tooltips / hover. All functions are pure and safe on undefined / NaN.
 */
import { format as dfFormat, formatDistanceStrict } from "date-fns";

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** |v| below this is dust (fee rounding, 1e-14 token remainders) and renders as 0. */
export const DUST = 1e-9;

/** Above this (1000 T) the suffix scale stops and a plain thousands-separated number is shown. */
const SUFFIX_MAX = 1e15;

const SUFFIXES: [number, string][] = [
  [1e12, "T"],
  [1e9, "B"],
  [1e6, "M"],
  [1e3, "K"],
];

/**
 * `digits` significant digits of a non-negative number without exponent
 * notation: toPrecision switches to "1.23e-7" below 1e-6, so tiny values go
 * through toFixed with the matching number of decimals.
 */
function sig(abs: number, digits: number): string {
  if (abs >= 1e-6) return trimZeros(abs.toPrecision(digits));
  const decimals = Math.min(20, Math.max(0, digits - 1 - Math.floor(Math.log10(abs))));
  return trimZeros(abs.toFixed(decimals));
}

/** Plain thousands-separated integer, never exponent notation (Intl handles ≥ 1e21). */
function plainInt(v: number): string {
  return v.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

/** 1234567 -> "1.23M"; 999 -> "999"; 0.0001234 -> "0.000123"; |v| < 1e-9 -> "0"; ≥ 1e15 plain. */
export function fmtCompact(v: number | undefined | null, digits = 3): string {
  if (!isNum(v)) return "–";
  const abs = Math.abs(v);
  if (abs < DUST) return "0";
  if (abs >= SUFFIX_MAX) return plainInt(v);
  for (const [k, s] of SUFFIXES) {
    if (abs >= k) {
      const m = v / k;
      // never fewer significant digits than the integer part has (avoids "7.3e+2K")
      const intDigits = Math.floor(Math.log10(Math.abs(m))) + 1;
      return trimZeros(m.toPrecision(Math.max(digits, intDigits))) + s;
    }
  }
  if (abs >= 1) return trimZeros(v.toPrecision(Math.max(digits, 3)));
  return (v < 0 ? "-" : "") + sig(abs, digits);
}

/** Exact, thousands-separated, up to `maxFrac` fraction digits (default 6). */
export function fmtExact(v: number | undefined | null, maxFrac = 6): string {
  if (!isNum(v)) return "–";
  return v.toLocaleString("en-US", { maximumFractionDigits: maxFrac });
}

/** "$1.23M", "-$45.6K", "$0.42". Sign kept. */
export function fmtUsd(v: number | undefined | null, digits = 3): string {
  if (!isNum(v)) return "–";
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  if (abs < DUST) return "$0";
  if (abs >= 1000) return `${sign}$${fmtCompact(abs, digits)}`;
  if (abs >= 1) return `${sign}$${abs.toFixed(2)}`;
  return `${sign}$${sig(abs, 3)}`;
}

/** Half a cent: below it a dollar result rounds to zero cents. */
export const HALF_CENT = 0.005;

/** True when `v` rounds to $0.00 (a gain of −$0.000001 is no gain). */
export function isZeroCents(v: number | undefined | null): boolean {
  return isNum(v) && Math.abs(v) < HALF_CENT;
}

/**
 * USD rounded to cents, for gains and results where a sub-cent value is
 * rounding noise, not a figure: |v| < $0.005 -> "$0.00" (no sign),
 * |v| < $1 -> "$0.42", otherwise as `fmtUsd`. Prices and amounts keep
 * `fmtUsd`, which shows sub-cent values on purpose.
 */
export function fmtUsdCents(v: number | undefined | null, digits = 3): string {
  if (!isNum(v)) return "–";
  if (isZeroCents(v)) return "$0.00";
  const abs = Math.abs(v);
  if (abs < 1) return `${v < 0 ? "-" : ""}$${abs.toFixed(2)}`;
  return fmtUsd(v, digits);
}

/** Exact USD with 2 decimals (or more for sub-cent prices). */
export function fmtUsdExact(v: number | undefined | null): string {
  if (!isNum(v)) return "–";
  const abs = Math.abs(v);
  if (abs < DUST) return "$0";
  const frac = abs >= 0.01 ? 2 : Math.min(10, 2 - Math.floor(Math.log10(abs)));
  return (
    (v < 0 ? "-" : "") +
    "$" +
    abs.toLocaleString("en-US", { minimumFractionDigits: frac, maximumFractionDigits: frac })
  );
}

/** Token price: adaptive significant digits so tiny prices stay readable. */
export function fmtPrice(v: number | undefined | null, currency = "$"): string {
  if (!isNum(v)) return "–";
  const abs = Math.abs(v);
  let s: string;
  if (abs < DUST) s = "0";
  else if (abs >= 1000) s = fmtCompact(abs, 4);
  else if (abs >= 1) s = abs.toFixed(abs >= 100 ? 2 : 4);
  else s = sig(abs, 4);
  return (v < 0 ? "-" : "") + currency + s;
}

/** Token amount, compact, with optional symbol. */
export function fmtAmount(v: number | undefined | null, symbol?: string, digits = 4): string {
  if (!isNum(v)) return "–";
  const s = fmtCompact(v, digits);
  return symbol ? `${s} ${symbol}` : s;
}

/** Fractions 0..1 -> "12.3%"; return multiples ("_pct" fields) also fit: 0.12 -> "+12.0%". */
export function fmtPct(v: number | undefined | null, opts: { sign?: boolean; digits?: number } = {}): string {
  if (!isNum(v)) return "–";
  const digits = opts.digits ?? 1;
  const p = v * 100;
  const s = Math.abs(p) >= 1000 ? fmtCompact(p, 3) : p.toFixed(digits);
  return (opts.sign && p > 0 ? "+" : "") + s + "%";
}

export function fmtInt(v: number | undefined | null): string {
  if (!isNum(v)) return "–";
  return Math.round(v).toLocaleString("en-US");
}

export function fmtBlock(v: number | undefined | null): string {
  if (!isNum(v)) return "–";
  return `#${Math.round(v).toLocaleString("en-US")}`;
}

/** 0xabcdef...1234 */
export function shortAddress(a: string | undefined | null, head = 6, tail = 4): string {
  if (!a) return "–";
  if (a.length <= head + tail + 2) return a;
  return `${a.slice(0, head)}…${a.slice(-tail)}`;
}

export function shortTx(tx: string | undefined | null): string {
  return shortAddress(tx, 8, 6);
}

/** Unix seconds -> local "2026-09-08 14:03:11". */
export function fmtTime(t: number | undefined | null, pattern = "yyyy-MM-dd HH:mm:ss"): string {
  if (!isNum(t)) return "–";
  return dfFormat(new Date(t * 1000), pattern);
}

/** Unix seconds -> "2026-09-08 12:03:11 UTC" (for hover titles; the cell shows local time). */
export function fmtUtc(t: number | undefined | null): string {
  if (!isNum(t)) return "–";
  return new Date(t * 1000)
    .toISOString()
    .replace("T", " ")
    .replace(/\.\d{3}Z$/, " UTC");
}

/** Time without date when the range is intraday. */
export function fmtTimeShort(t: number | undefined | null): string {
  return fmtTime(t, "MM-dd HH:mm");
}

/**
 * Narrow table cells: "HH:mm:ss" for today (local), "MM-dd HH:mm" earlier
 * this year, "yyyy-MM-dd" for another year (touch screens have no hover
 * title, so the year must stay visible).
 */
export function fmtTimeCompact(t: number | undefined | null, now = Date.now() / 1000): string {
  if (!isNum(t)) return "–";
  const [day, ref] = [new Date(t * 1000), new Date(now * 1000)];
  if (day.getFullYear() !== ref.getFullYear()) return fmtTime(t, "yyyy-MM-dd");
  const today = dfFormat(ref, "MM-dd") === dfFormat(day, "MM-dd");
  return fmtTime(t, today ? "HH:mm:ss" : "MM-dd HH:mm");
}

/** "3 minutes ago"-style relative time from unix seconds. */
export function fmtAgo(t: number | undefined | null, now = Date.now() / 1000): string {
  if (!isNum(t)) return "–";
  return formatDistanceStrict(new Date(t * 1000), new Date(now * 1000), { addSuffix: true });
}

/** "21m ago"-style compact relative time from unix seconds, for phone cells. */
export function fmtAgoShort(t: number | undefined | null, now = Date.now() / 1000): string {
  if (!isNum(t)) return "–";
  return `${fmtDuration(now - t)} ago`;
}

/** Seconds -> "1h 5m", "3d", "45s". */
export function fmtDuration(secs: number | undefined | null): string {
  if (!isNum(secs)) return "–";
  const s = Math.abs(Math.round(secs));
  if (s < 60) return `${s}s`;
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m && !d) parts.push(`${m}m`);
  return parts.join(" ") || `${s}s`;
}

/** Window string "24h" -> seconds (as label-bot parses; floor 60 s). Returns undefined on garbage. */
export function parseWindow(w: string | undefined | null): number | undefined {
  if (!w) return undefined;
  const m = /^(\d+(?:\.\d+)?)\s*([mhdw])$/i.exec(w.trim());
  if (!m) return undefined;
  const n = Number(m[1]);
  const unit = { m: 60, h: 3600, d: 86400, w: 604800 }[m[2]!.toLowerCase() as "m" | "h" | "d" | "w"];
  return Math.max(60, Math.round(n * unit));
}

export function bytes(v: number | undefined | null): string {
  if (!isNum(v)) return "–";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let x = v;
  while (x >= 1024 && i < units.length - 1) {
    x /= 1024;
    i++;
  }
  return `${i === 0 ? x : x.toFixed(1)} ${units[i]}`;
}

function trimZeros(s: string): string {
  if (s.includes("e")) return s;
  return s.includes(".") ? s.replace(/\.?0+$/, "") : s;
}
